import type { ScanResult } from './elm327.ts';
import type { RecallLookup } from './api.ts';
import type { UnitSystem } from './obd/pids.ts';

// Plain-language findings.
//
// Every other panel reports what the vehicle said. This one says what it means
// and what to do about it, for someone who does not know what a monitor is.
//
// Two rules shape the wording. Nothing here invents certainty the data does not
// support, so a code is "worth investigating" rather than "your catalyst is
// dead". And every finding ends with an action, because a person reading this
// is deciding whether to drive it, buy it, or book it in.

export type Verdict = 'critical' | 'attention' | 'watch' | 'good';

export interface Finding {
  severity: Verdict;
  title: string;
  detail: string;
  /** The technical basis, for anyone who wants to check the reasoning. */
  evidence?: string;
}


export interface Summary {
  verdict: Verdict;
  headline: string;
  findings: Finding[];
}

const RANK: Record<Verdict, number> = { critical: 0, attention: 1, watch: 2, good: 3 };

/** Distance in whichever system the reader picked, since it lands in prose. */
function distance(km: number, units: UnitSystem): string {
  return units === 'imperial' ? `${Math.round(km * 0.621371)} miles` : `${Math.round(km)} km`;
}

export function summarise(result: ScanResult, recalls: RecallLookup | null, units: UnitSystem): Summary {
  const findings: Finding[] = [];

  const stored = result.stored;
  const pending = result.pending;
  const critical = stored.filter((d) => d.severity === 'critical');
  const high = stored.filter((d) => d.severity === 'high');

  // ---- fault codes ----
  if (critical.length > 0) {
    findings.push({
      severity: 'critical',
      title: `${critical.length === 1 ? 'A fault' : `${critical.length} faults`} that can damage the engine`,
      detail:
        'Codes in this group are the kind that get worse with driving. Have it looked at before any long trip, and stop driving it if you notice misfiring, loss of power or unusual noise.',
      evidence: critical.map((d) => `${d.code} ${d.description}`).join(' · '),
    });
  }

  if (high.length > 0) {
    findings.push({
      severity: 'attention',
      title: `${high.length === 1 ? 'A fault' : `${high.length} faults`} needing repair`,
      detail: 'Safe to drive in the short term in most cases, but this will not fix itself. Book it in.',
      evidence: high.map((d) => `${d.code} ${d.description}`).join(' · '),
    });
  }

  const otherStored = stored.filter((d) => d.severity !== 'critical' && d.severity !== 'high');
  if (otherStored.length > 0) {
    findings.push({
      severity: 'watch',
      title: `${otherStored.length} minor fault${otherStored.length === 1 ? '' : 's'} stored`,
      detail:
        'Usually emissions-related rather than mechanical. Worth fixing before an emissions test, and worth quoting before you buy.',
      evidence: otherStored.map((d) => `${d.code} ${d.description}`).join(' · '),
    });
  }

  if (pending.length > 0) {
    findings.push({
      severity: 'watch',
      title: `${pending.length} intermittent fault${pending.length === 1 ? '' : 's'} developing`,
      detail:
        'The car has seen this go wrong once but not consistently. It becomes a real fault, with a warning light, if it happens again.',
      evidence: pending.map((d) => `${d.code} ${d.description}`).join(' · '),
    });
  }

  if (result.milOn && stored.length === 0) {
    findings.push({
      severity: 'attention',
      title: 'Warning light on with no codes stored',
      detail:
        'Unusual. It can mean a module was not read, or that a fault was cleared without the lamp resetting. Worth a second scan with the engine running.',
    });
  }

  // ---- emissions readiness, and the used-car tell ----
  const readiness = result.readiness;
  const clearedKm = result.vitals.distClearedKm;
  const recentlyCleared = typeof clearedKm === 'number' && clearedKm > 0 && clearedKm < 80;

  if (readiness && !readiness.ready) {
    const incomplete = readiness.monitors.filter((m) => m.supported && !m.complete).map((m) => m.name);

    if (recentlyCleared) {
      // Codes wiped, monitors not yet finished, and barely any distance since.
      // On a car being sold, that combination hides whatever was showing.
      findings.push({
        severity: 'critical',
        title: 'Fault codes were cleared very recently',
        detail:
          'The self-tests have not finished running and the car has covered almost no distance since the codes were reset. Any fault that was showing would be invisible right now. If you are considering buying this vehicle, drive it for a week and scan it again before deciding.',
        evidence: `${distance(clearedKm, units)} since codes cleared · ${incomplete.length} monitor(s) incomplete`,
      });
    } else {
      findings.push({
        severity: 'attention',
        title: 'Would fail an emissions test today',
        detail:
          `Not a fault. The car runs self-checks in the background and some have not finished, which happens after a battery disconnect or a code reset. Around ${units === 'imperial' ? '50 to 100 miles' : '80 to 160 km'} of mixed driving usually completes them.`,
        evidence: `Incomplete: ${incomplete.join(', ')}`,
      });
    }
  } else if (readiness?.ready && stored.length === 0) {
    findings.push({
      severity: 'good',
      title: 'Ready for an emissions test',
      detail: 'Every self-check the vehicle runs has completed, and nothing is stored against it.',
    });
  }

  // ---- recalls ----
  if (recalls && !recalls.unresolved && recalls.openCount > 0) {
    findings.push({
      severity: 'attention',
      title: `${recalls.openCount} open safety recall${recalls.openCount === 1 ? '' : 's'}`,
      detail:
        'The manufacturer repairs these free of charge, whoever owns the car and regardless of age or mileage. Call any franchised dealer with the VIN to confirm which apply and book them.',
      evidence: recalls.recalls
        .filter((r) => !r.status || r.status.status === 'open' || r.status.status === 'scheduled')
        .slice(0, 4)
        .map((r) => `${r.campaignNumber} ${r.component ?? ''}`.trim())
        .join(' · '),
    });
  } else if (recalls?.unresolved) {
    findings.push({
      severity: 'watch',
      title: 'Recalls could not be checked',
      detail:
        'Treat this as unknown rather than clear. Check the VIN on the manufacturer or NHTSA website before relying on it.',
      evidence: recalls.notice,
    });
  }

  // ---- fuel trim, the most useful live-data tell ----
  const trims = [
    { label: 'short term', value: result.vitals.stftB1 },
    { label: 'long term', value: result.vitals.ltftB1 },
  ].filter((t): t is { label: string; value: number } => typeof t.value === 'number');

  const worstTrim = trims.reduce<{ label: string; value: number } | null>(
    (worst, t) => (worst === null || Math.abs(t.value) > Math.abs(worst.value) ? t : worst),
    null,
  );

  if (worstTrim && Math.abs(worstTrim.value) >= 10) {
    const lean = worstTrim.value > 0;
    findings.push({
      severity: 'watch',
      title: lean ? 'Engine is adding extra fuel to compensate' : 'Engine is cutting fuel to compensate',
      detail: lean
        ? 'Usually an air leak, a dirty air-flow sensor, or weak fuel delivery. Often shows up before any warning light does.'
        : 'Usually a leaking injector, high fuel pressure, or a sensor reading incorrectly.',
      evidence: `${worstTrim.label} fuel trim ${worstTrim.value > 0 ? '+' : ''}${worstTrim.value}%`,
    });
  }

  // ---- charging system ----
  const volts = result.vitals.voltage ?? result.adapterVoltage;
  const running = (result.vitals.rpm ?? 0) > 400;
  if (typeof volts === 'number') {
    if (running && volts < 13.2) {
      findings.push({
        severity: 'attention',
        title: 'Charging system is not keeping up',
        detail: 'With the engine running this should sit above 13.5 volts. Have the alternator and battery tested.',
        evidence: `${volts.toFixed(1)} V with the engine running`,
      });
    } else if (!running && volts < 12.2) {
      findings.push({
        severity: 'watch',
        title: 'Battery is low',
        detail: 'A healthy battery reads about 12.6 volts with the engine off. This may struggle to start when cold.',
        evidence: `${volts.toFixed(1)} V with the engine off`,
      });
    }
  }

  // ---- on-board self-test measurements ----
  const failing = result.monitorTests.filter((t) => !t.invalidReason && t.pass === false);
  if (failing.length > 0) {
    findings.push({
      severity: 'watch',
      title: `${failing.length} self-test${failing.length === 1 ? '' : 's'} outside the normal range`,
      detail:
        'The car measured these itself and they are drifting, though not far enough to set a warning light yet. Useful as an early warning rather than a fault.',
      evidence: [...new Set(failing.map((t) => t.monitorLabel))].slice(0, 4).join(' · '),
    });
  }

  // ---- nothing wrong ----
  if (findings.every((f) => f.severity === 'good')) {
    findings.unshift({
      severity: 'good',
      title: 'No faults found',
      detail:
        'Nothing stored, nothing pending, and no warning light. This is a snapshot of today rather than a guarantee, but it is a clean result.',
    });
  }

  findings.sort((a, b) => RANK[a.severity] - RANK[b.severity]);
  const verdict = findings[0]?.severity ?? 'good';

  return { verdict, headline: headlineFor(verdict, findings), findings };
}

function headlineFor(verdict: Verdict, findings: Finding[]): string {
  const actionable = findings.filter((f) => f.severity !== 'good').length;
  switch (verdict) {
    case 'critical':
      return 'Needs attention before driving far';
    case 'attention':
      return `${actionable} thing${actionable === 1 ? '' : 's'} to deal with`;
    case 'watch':
      return 'Nothing urgent, a few things to keep an eye on';
    default:
      return 'Looks healthy';
  }
}
